import type { Block, Task, TaskVariable } from '@/types/experience';

import { isVarUsed, isVarUsedByFormula, renameVarInBlockData, renameVarInFormula } from '@/components/task/variable-utils';

/**
 * RENAMING A VARIABLE IN A TASK NOBODY HAS OPEN.
 *
 * Chips store the NAME — a formula holds `{river}` and a richtext chip holds
 * `data-variable-chip="river"` — so a rename is not a change to one row, it is a rewrite of every
 * block that mentions it. Inside the open task that is what `handleUpdateVariable` has always
 * done. For a variable the whole experience can see, those blocks are in tasks that are not
 * loaded for editing, and until now the rename was simply REFUSED for that reason: a sweep of
 * only the open task would leave the rest pointing at a name that no longer exists.
 *
 * Planning is separated from writing so the hard part is answerable without a network: given a
 * task and a rename, this returns exactly what that task should become, and the caller decides
 * whether and how to send it.
 *
 * `expectedRevision` rides along because that is what makes writing a task you are not editing
 * safe. The page holds a copy of every task from the experience it loaded, and someone else may
 * have saved since; sending the revision turns that from a silent clobber into a 409 the caller
 * can report. It is the first time this app writes another task's blocks, so that guard is the
 * whole basis for doing it at all.
 */
export interface TaskRenamePlan {
  taskId: string;
  /** For telling the teacher which tasks were touched, or which one refused. */
  title: string;
  blocks: Block[];
  variables: TaskVariable[];
  expectedRevision: number;
}

/** What this task becomes, or `null` when the name appears nowhere in it and nothing need be sent. */
export function planTaskRename(
  task: Pick<Task, 'id' | 'title' | 'blocks' | 'variables' | 'contentRevision'>,
  oldName: string,
  newName: string,
  varId?: string,
): TaskRenamePlan | null {
  if (!oldName || !newName || oldName === newName) return null;

  const blocks = task.blocks ?? [];
  const variables = task.variables ?? [];
  /*
   * Asked BEFORE rewriting, not by comparing after: `renameVarInBlockData` rebuilds every data
   * object whether or not a string in it matched, so the result is always a new object and
   * identity says nothing about whether anything moved.
   */
  const mentionsName = isVarUsed(oldName, blocks);
  const definesName = variables.some(v => v.name === oldName);
  // A sibling EQUATION mentions the name too, and it is the one dependent that lives in the
  // variables list rather than in block data. Missing it renames the row and leaves
  // `luminosity = {mass}^3.5` pointing at a `mass` that no longer exists — which resolves to
  // nothing, silently, in a task nobody has open.
  const dependsOnName = isVarUsedByFormula(oldName, variables);
  if (!mentionsName && !definesName && !dependsOnName) return null;

  return {
    taskId: task.id,
    title: task.title,
    blocks: mentionsName
      ? blocks.map(b => ({ ...b, data: renameVarInBlockData(b.data as Record<string, unknown>, oldName, newName, varId) }))
      : blocks,
    // A task holding its OWN variable of that name is the promotion's "keep separate": the row
    // moves with its chips. For an experience variable there is no row here and only chips move.
    variables: definesName || dependsOnName
      ? variables.map(v => ({
          ...v,
          ...(v.name === oldName ? { name: newName } : {}),
          ...(v.formula ? { formula: renameVarInFormula(v.formula, oldName, newName) } : {}),
        }))
      : variables,
    expectedRevision: task.contentRevision ?? 1,
  };
}

/** Every task that has to change for one rename, the open one excluded — the caller owns that. */
export function planRenameAcrossTasks(
  tasks: Pick<Task, 'id' | 'title' | 'blocks' | 'variables' | 'contentRevision'>[],
  exceptTaskId: string | undefined,
  oldName: string,
  newName: string,
  varId?: string,
): TaskRenamePlan[] {
  return tasks
    .filter(t => t.id !== exceptTaskId)
    .map(t => planTaskRename(t, oldName, newName, varId))
    .filter((p): p is TaskRenamePlan => p !== null);
}

/** A name nothing in the experience is using, so a "keep separate" lands somewhere free. */
export function freeVariableName(base: string, taken: Iterable<string>): string {
  const used = new Set(taken);
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

/**
 * EVERY member of a renamed set, in ONE plan per task.
 *
 * Calling `planRenameAcrossTasks` once per member and firing the results was the bug this
 * replaces. Each call read `contentRevision` from the same loaded copy, so all N plans for a
 * task carried the SAME `expectedRevision`: the first PUT landed, bumped the revision, and every
 * other one came back 409. The teacher saw "rename refused" and a set left half-moved, with the
 * members that failed still sitting under the old name — a new set for every attempt.
 *
 * One plan per task fixes it at the root: the pairs are folded onto a working copy of the task,
 * so the single write carries every member's rename and the one revision it was planned against.
 */
export function planSetRenameAcrossTasks(
  tasks: Pick<Task, 'id' | 'title' | 'blocks' | 'variables' | 'contentRevision'>[],
  exceptTaskId: string | undefined,
  pairs: readonly (readonly [string, string])[],
): TaskRenamePlan[] {
  return tasks
    .filter(t => t.id !== exceptTaskId)
    .flatMap(t => {
      let cur = t;
      let touched = false;
      for (const [oldName, newName] of pairs) {
        const step = planTaskRename(cur, oldName, newName);
        if (!step) continue;
        touched = true;
        cur = { ...cur, blocks: step.blocks, variables: step.variables };
      }
      // The revision comes from the ORIGINAL task, never from a step: the folding above is all
      // in memory and nothing has been written yet, so there is exactly one revision to expect.
      return touched
        ? [{ taskId: t.id, title: t.title, blocks: cur.blocks ?? [], variables: cur.variables ?? [], expectedRevision: t.contentRevision ?? 1 }]
        : [];
    });
}
